import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Clock, RotateCcw, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, XCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, LayoutGrid, Layers, Users, TrendingUp, List, LayoutList, User, FileText } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import { CustomDatePicker } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
  usePointageEmployeeOptions,
  resolvePointageEmployeeFromSelect,
  resolvePointageEmployeeDisplayName,
  resolvePointageEmployeeSelectValue,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import {
  formatPointageDate,
  RECUPERATION_STATUS_VALUES,
  RECUPERATION_REASON_VALUES,
  normalizeRecuperationStatus,
  normalizeRecuperationReason,
  getEndOfMonthDate,
  SAUDI_TOIL_RATIO,
} from './pointageHelpers';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const RecuperationsPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.recuperationsPage.${key}`, opts);

  const getStatusLabel = useCallback((status) => {
    const normalized = normalizeRecuperationStatus(status);
    const keys = {
      [RECUPERATION_STATUS_VALUES.to_recover]: 'status_to_recover',
      [RECUPERATION_STATUS_VALUES.in_progress]: 'status_in_progress',
      [RECUPERATION_STATUS_VALUES.recovered]: 'status_recovered',
      [RECUPERATION_STATUS_VALUES.expired]: 'status_expired',
    };
    return keys[normalized] ? p(keys[normalized]) : p('em_dash');
  }, [p]);

  const getReasonLabel = useCallback((reason) => {
    const normalized = normalizeRecuperationReason(reason);
    const keys = {
      [RECUPERATION_REASON_VALUES.overtime]: 'reason_overtime',
      [RECUPERATION_REASON_VALUES.weekend]: 'reason_weekend',
      [RECUPERATION_REASON_VALUES.holiday]: 'reason_holiday',
      [RECUPERATION_REASON_VALUES.other]: 'reason_other',
    };
    return keys[normalized] ? p(keys[normalized]) : reason;
  }, [p]);

  const [searchTerm, setSearchTerm] = useState('');
  const [recuperations, setRecuperations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');

  const RECUPERATION_FORM_FIELD_COUNT = 11;

  const recuperationFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_employee_info'), icon: User, iconClass: 'text-emerald-400' },
      { id: 'details', label: p('section_validation_notes'), icon: CheckCircle, iconClass: 'text-green-400' },
    ],
  }, RECUPERATION_FORM_FIELD_COUNT), [p]);
  
  const [formData, setFormData] = useState({ 
    employee_id: '',
    employee_name: '',
    hours_acquired: '',
    acquisition_date: '',
    acquisition_reason: '',
    hours_used: '',
    recovery_date: '',
    balance_hours: '',
    status: '',
    expiration_date: '',
    validator_name: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_recuperations');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadRecuperations(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalizeRecord = (record) => ({
    ...record,
    status: normalizeRecuperationStatus(record.status),
    acquisition_reason: normalizeRecuperationReason(record.acquisition_reason),
  });

  const loadRecuperations = async () => {
    try {
      const response = await localApiAdapter.get('/pointage/recuperations');
      const rows = response?.data || response || [];
      setRecuperations(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []).map(normalizeRecord));
    } catch (error) {
      console.error('Error loading recuperations:', error);
      setRecuperations([]);
    }
  };

  const stats = {
    total: recuperations.length,
    toRecover: recuperations.filter(r => normalizeRecuperationStatus(r.status) === RECUPERATION_STATUS_VALUES.to_recover).length,
    recovered: recuperations.filter(r => normalizeRecuperationStatus(r.status) === RECUPERATION_STATUS_VALUES.recovered).length,
    inProgress: recuperations.filter(r => normalizeRecuperationStatus(r.status) === RECUPERATION_STATUS_VALUES.in_progress).length,
    totalHours: recuperations.reduce((sum, r) => sum + (parseFloat(r.balance_hours) || 0), 0).toFixed(2)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_recuperations', name: p('mainTab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('mainTab_by_status'), icon: CheckCircle },
    { id: 'par_employe', name: p('mainTab_by_employee'), icon: Users }
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: RECUPERATION_STATUS_VALUES.to_recover, name: p('quickFilter_to_recover'), icon: Clock },
    { id: RECUPERATION_STATUS_VALUES.in_progress, name: p('quickFilter_in_progress'), icon: AlertTriangle },
    { id: RECUPERATION_STATUS_VALUES.recovered, name: p('quickFilter_recovered'), icon: CheckCircle },
    { id: RECUPERATION_STATUS_VALUES.expired, name: p('quickFilter_expired'), icon: XCircle }
  ]), [p]);

  const recuperationDistribution = useMemo(() => ({
    all_recuperations: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: RECUPERATION_STATUS_VALUES.to_recover, name: p('dist_to_recover'), count: stats.toRecover, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: RECUPERATION_STATUS_VALUES.in_progress, name: p('dist_in_progress'), count: stats.inProgress, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: RECUPERATION_STATUS_VALUES.recovered, name: p('dist_recovered'), count: stats.recovered, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: RECUPERATION_STATUS_VALUES.expired, name: p('dist_expired'), count: recuperations.filter(r => normalizeRecuperationStatus(r.status) === RECUPERATION_STATUS_VALUES.expired).length, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: RECUPERATION_STATUS_VALUES.to_recover, name: p('dist_to_recover'), count: stats.toRecover, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: RECUPERATION_STATUS_VALUES.in_progress, name: p('dist_in_progress'), count: stats.inProgress, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: RECUPERATION_STATUS_VALUES.recovered, name: p('dist_recovered'), count: stats.recovered, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: RECUPERATION_STATUS_VALUES.expired, name: p('dist_expired'), count: recuperations.filter(r => normalizeRecuperationStatus(r.status) === RECUPERATION_STATUS_VALUES.expired).length, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_employe: {
      title: p('dist_by_employee_title'),
      data: (() => {
        const employeeStats = {};
        recuperations.forEach(r => {
          const name = r.employee_name || p('unknown');
          if (!employeeStats[name]) {
            employeeStats[name] = 0;
          }
          employeeStats[name]++;
        });
        const colors = ["bg-gradient-to-r from-blue-500 to-blue-600", "bg-gradient-to-r from-green-500 to-green-600", "bg-gradient-to-r from-purple-500 to-purple-600", "bg-gradient-to-r from-orange-500 to-orange-600"];
        return Object.entries(employeeStats).slice(0, 4).map(([name, count], idx) => ({
          filterKey: name,
          name,
          count,
          color: colors[idx % colors.length]
        }));
      })()
    }
  }), [t, recuperations, stats.toRecover, stats.inProgress, stats.recovered]);

  const distributionInfo = recuperationDistribution[mainViewMode] || recuperationDistribution.all_recuperations;

  const baseFilteredRecuperations = useMemo(() => recuperations.filter(r => {
    const recordStatus = normalizeRecuperationStatus(r.status);
    if (statusFilter !== 'all' && recordStatus !== normalizeRecuperationStatus(statusFilter)) return false;

    if (dateRange.start || dateRange.end) {
      const acquisitionDate = new Date(r.acquisition_date);

      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (acquisitionDate < startOfDay) return false;
      }

      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (acquisitionDate > endOfDay) return false;
      }
    }

    if (searchTerm && !r.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.validator_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'all_recuperations') {
      if (quickFilter !== 'all' && recordStatus !== quickFilter) return false;
    }
    if (activeDistributionFilter) {
      if (activeDistributionFilter !== recordStatus && mainViewMode === 'par_statut') return false;
      if (mainViewMode === 'par_employe' && activeDistributionFilter !== r.employee_name) return false;
    }
    return true;
  }), [recuperations, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('employee_name', (r) => r.employee_name || ''),
      textColumnRule('hours_acquired', (r) => String(r.hours_acquired ?? '')),
      textColumnRule('acquisition_date', (r) => formatPointageDate(r.acquisition_date) || ''),
      textColumnRule('hours_used', (r) => String(r.hours_used ?? '')),
      textColumnRule('recovery_date', (r) => formatPointageDate(r.recovery_date) || ''),
      textColumnRule('balance_hours', (r) => String(r.balance_hours ?? '')),
      exactColumnRule('status', (r) => normalizeRecuperationStatus(r.status)),
    ],
    [getStatusLabel],
  );

  const filteredRecuperations = useMemo(
    () => applyColumnFilters(baseFilteredRecuperations, colFilters, columnFilterRules),
    [baseFilteredRecuperations, colFilters, columnFilterRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const acquired = parseFloat(formData.hours_acquired) || 0;
      const used = parseFloat(formData.hours_used) || 0;
      const expirationDate = formData.expiration_date || (formData.acquisition_date ? getEndOfMonthDate(formData.acquisition_date) : '');
      const submitData = { 
        ...formData,
        employee_id: formData.employee_id,
        employee_name: formData.employee_name
          || resolvePointageEmployeeDisplayName(formData.employee_id, employeeOptions, ''),
        status: normalizeRecuperationStatus(formData.status) || RECUPERATION_STATUS_VALUES.to_recover,
        acquisition_reason: normalizeRecuperationReason(formData.acquisition_reason) || RECUPERATION_REASON_VALUES.overtime,
        expiration_date: expirationDate,
        balance_hours: (acquired * SAUDI_TOIL_RATIO) - used,
        toil_ratio: SAUDI_TOIL_RATIO,
      };
      if (editingRecord) {
        await localApiAdapter.put(`/pointage/recuperations/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/pointage/recuperations', submitData);
        notifyCreated(showNotify, p('title'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error')); return;
    } finally {
      resetForm();
      loadRecuperations();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/pointage/recuperations/${id}`);
        notifyDeleted(showNotify, p('title'));
        loadRecuperations();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/pointage/recuperations/${id}`, { status: newStatus });
      loadRecuperations();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/pointage/recuperations/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        loadRecuperations();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      employee_id: '', employee_name: '', hours_acquired: '', acquisition_date: '', acquisition_reason: '',
      hours_used: '', recovery_date: '', balance_hours: '', status: '', expiration_date: '', validator_name: '', notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredRecuperations);


  const POINTAGE_TABLE_COLUMNS = [
    { key: 'employee_name', labelKey: 'col_employee' },
    { key: 'hours_acquired', labelKey: 'col_hours_acquired' },
    { key: 'acquisition_date', labelKey: 'col_acquisition_date' },
    { key: 'hours_used', labelKey: 'col_hours_used' },
    { key: 'recovery_date', labelKey: 'col_recovery_date' },
    { key: 'balance_hours', labelKey: 'col_balance' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Clock className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadRecuperations} className="refresh-button" title={t('common.refresh')}><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
          <p className="text-white/50 text-sm mt-1">{p('toil_policy_hint', { ratio: '1:1' })}</p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, label: `${t('common.delete')} (${selectedRecords.length})`, title: t('common.delete', { defaultValue: 'حذف' }) })}
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_record')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: Clock, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_to_recover'), value: stats.toRecover, icon: AlertTriangle, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_recovered'), value: stats.recovered, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_in_progress'), value: stats.inProgress, icon: RotateCcw, color: 'from-yellow-500 to-yellow-600' },
          { title: p('stat_total_hours'), value: `${stats.totalHours}${p('hours_suffix')}`, icon: TrendingUp, color: 'from-purple-500 to-purple-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_recuperations' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })()}{p('percent_of_total')}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })} deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })} />
                  {isColVisible('employee_name') && (
                    <TableColumnHeaderFilter
                      columnKey="employee_name"
                      label={p('col_employee')}
                      value={colFilters.employee_name ?? ''}
                      onChange={(v) => setColFilter('employee_name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white"
                    />
                  )}
                  {isColVisible('hours_acquired') && (
                    <TableColumnHeaderFilter
                      columnKey="hours_acquired"
                      label={p('col_hours_acquired')}
                      value={colFilters.hours_acquired ?? ''}
                      onChange={(v) => setColFilter('hours_acquired', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white"
                    />
                  )}
                  {isColVisible('acquisition_date') && (
                    <TableColumnHeaderFilter
                      columnKey="acquisition_date"
                      label={p('col_acquisition_date')}
                      value={colFilters.acquisition_date ?? ''}
                      onChange={(v) => setColFilter('acquisition_date', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white"
                    />
                  )}
                  {isColVisible('hours_used') && (
                    <TableColumnHeaderFilter columnKey="hours_used" label={p('col_hours_used')} value={colFilters.hours_used ?? ''} onChange={(v) => setColFilter('hours_used', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('recovery_date') && (
                    <TableColumnHeaderFilter columnKey="recovery_date" label={p('col_recovery_date')} value={colFilters.recovery_date ?? ''} onChange={(v) => setColFilter('recovery_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('balance_hours') && (
                    <TableColumnHeaderFilter
                      columnKey="balance_hours"
                      label={p('col_balance')}
                      value={colFilters.balance_hours ?? ''}
                      onChange={(v) => setColFilter('balance_hours', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white"
                    />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter
                      columnKey="status"
                      label={p('col_status')}
                      kind="select"
                      options={statusColumnFilterOptions}
                      value={colFilters.status ?? ''}
                      onChange={(v) => setColFilter('status', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-white"
                    />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredRecuperations.length > 0 ? (
                  filteredRecuperations.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('employee_name') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{resolvePointageEmployeeDisplayName(record.employee_id, employeeOptions, record.employee_name || '-')}</span></td>
                      )}
                      {isColVisible('hours_acquired') && (
                        <td className="px-6 py-4"><span className="text-white font-bold text-lg">{record.hours_acquired || 0}{p('hours_suffix')}</span></td>
                      )}
                      {isColVisible('acquisition_date') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.acquisition_date ? formatPointageDate(record.acquisition_date) : '-'}</span></td>
                      )}
                      {isColVisible('hours_used') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.hours_used || 0}{p('hours_suffix')}</span></td>
                      )}
                      {isColVisible('recovery_date') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.recovery_date ? formatPointageDate(record.recovery_date) : '-'}</span></td>
                      )}
                      {isColVisible('balance_hours') && (
                        <td className="px-6 py-4"><span className="text-white font-bold text-lg">{record.balance_hours || 0}{p('hours_suffix')}</span></td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={normalizeRecuperationStatus(record.status)}
                              recordId={record.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Clock size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredRecuperations.length })}</span>
                      <span>{p('footer_hours_acquired')} <span className="text-white font-semibold">{filteredRecuperations.reduce((sum, r) => sum + (parseFloat(r.hours_acquired) || 0), 0).toFixed(2)}{p('hours_suffix')}</span></span>
                      <span>{p('footer_balance_hours')} <span className="text-white font-semibold">{filteredRecuperations.reduce((sum, r) => sum + (parseFloat(r.balance_hours) || 0), 0).toFixed(2)}{p('hours_suffix')}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredRecuperations.length > 0 ? (
              filteredRecuperations.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Clock size={20} className="text-emerald-400" />{record.employee_name || p('not_available')}</h3>
                        <div className="flex">{getStatusBadge(record.status)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_employee_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.employee_name || '-'}</div>
                            <div><span className="font-medium">{p('label_validator')}</span> {record.validator_name || '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_hours')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_acquired')}</span> <span className="text-white font-bold">{record.hours_acquired || 0}{p('hours_suffix')}</span></div>
                            <div><span className="font-medium">{p('label_used')}</span> {record.hours_used || 0}{p('hours_suffix')}</div>
                            <div><span className="font-medium">{p('label_balance')}</span> <span className="text-green-400 font-bold">{record.balance_hours || 0}{p('hours_suffix')}</span></div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_acquired_on')}</span> {record.acquisition_date ? formatPointageDate(record.acquisition_date) : '-'}</div>
                            <div><span className="font-medium">{p('label_recovered_on')}</span> {record.recovery_date ? formatPointageDate(record.recovery_date) : '-'}</div>
                            <div><span className="font-medium">{p('label_expires_on')}</span> {record.expiration_date ? formatPointageDate(record.expiration_date) : '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_details')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_reason')}</span> {getReasonLabel(record.acquisition_reason) || '-'}</div>
                            <div><span className="font-medium">{p('label_status')}</span> {getStatusLabel(record.status) || '-'}</div>
                            {record.notes && <div className="pt-2 border-t border-white/20"><span className="font-medium">{p('label_notes')}</span> <p className="text-white/60 text-xs mt-1">{record.notes}</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Clock size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="recuperations-view-modal"
        >
          <div className="space-y-4">
                <div className={modalForm.viewCard}>
                  <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><User size={18} className="text-emerald-400" />{p('section_employee_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('col_employee')}</p><p className={modalForm.viewValue}>{viewingRecord.employee_name || '-'}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_validator')}</p><p className={modalForm.viewValue}>{viewingRecord.validator_name || '-'}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_status')}</p><div className="inline-flex">{getStatusBadge(viewingRecord.status)}</div></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_hours')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('label_hours_acquired')}</p><p className={`${modalForm.viewValue} font-bold text-2xl`}>{viewingRecord.hours_acquired || 0}{p('hours_suffix')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_hours_used')}</p><p className={modalForm.viewValue}>{viewingRecord.hours_used || 0}{p('hours_suffix')}</p></div>
                      <div className={`pt-2 border-t ${cfgTheme.light ? 'border-slate-200' : 'border-white/20'}`}><p className={modalForm.viewLabel}>{p('label_balance')}</p><p className="text-green-400 font-bold text-2xl">{viewingRecord.balance_hours || 0}{p('hours_suffix')}</p></div>
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_dates_reason')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('label_acquisition_date')}</p><p className={modalForm.viewValue}>{viewingRecord.acquisition_date ? formatPointageDate(viewingRecord.acquisition_date) : '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_recovery_date')}</p><p className={modalForm.viewValue}>{viewingRecord.recovery_date ? formatPointageDate(viewingRecord.recovery_date) : '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_expiration_date')}</p><p className={modalForm.viewValue}>{viewingRecord.expiration_date ? formatPointageDate(viewingRecord.expiration_date) : '-'}</p></div>
                      <div className={`pt-2 border-t ${cfgTheme.light ? 'border-slate-200' : 'border-white/20'}`}><p className={modalForm.viewLabel}>{p('label_reason')}</p><p className={modalForm.viewValue}>{getReasonLabel(viewingRecord.acquisition_reason) || '-'}</p></div>
                    </div>
                  </div>
                </div>
                {viewingRecord.notes && (
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_notes')}</h3>
                    <p className={`text-sm ${cfgTheme.light ? 'text-slate-600' : 'text-white/70'}`}>{viewingRecord.notes}</p>
                  </div>
                )}
          </div>
        </QawaemErpViewModal>
      )}

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingRecord ? p('modal_edit') : p('modal_new')}
        icon={Clock}
        showSaveButton
        formId="recuperation-form"
        saveLabel={editingRecord ? p('update') : p('create')}
        testId="recuperations-crud-modal"
        maxWidth="1200px"
        tabs={recuperationFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
      >
        <form id="recuperation-form" onSubmit={handleSubmit}>
          {pointageFormTabVisible(recuperationFormTabs, formTab, 'general') && (
            <div className="space-y-4">
            <div className={POINTAGE_FORM_GRID_CLASS}>
              <div><label className={modalForm.label}>{<RequiredLabelText text={p('label_employee_name_required')} />}</label><CustomSelect value={resolvePointageEmployeeSelectValue(formData, employeeOptions)} onChange={(value) => { const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(value, employeeOptions); setFormData({ ...formData, employee_id: employeeId, employee_name: employeeName }); }} options={employeeOptions} placeholder={p('placeholder_name')} searchable={true} creatable={false} autoSelect={false} /></div>
              <div><label className={modalForm.label}>{p('col_validator')}</label><CustomSelect value={formData.validator_name} onChange={(value) => setFormData({ ...formData, validator_name: value })} options={employeeOptions} placeholder={p('placeholder_name')} searchable={true} creatable={true} autoSelect={false} /></div>
            </div>
            <div className={POINTAGE_FORM_GRID_CLASS}>
              <div><label className={modalForm.label}>{<RequiredLabelText text={p('label_hours_acquired_required')} />}</label><CustomNumberInput step={0.5} value={formData.hours_acquired || ''} onChange={(value) => setFormData({ ...formData, hours_acquired: value })} placeholder={p('placeholder_number')} /></div>
              <div><label className={modalForm.label}>{p('label_hours_used')}</label><CustomNumberInput step={0.5} value={formData.hours_used || ''} onChange={(value) => setFormData({ ...formData, hours_used: value })} placeholder={p('placeholder_number')} /></div>
              <div><label className={modalForm.label}>{p('label_balance_calculated')}</label><input type="text" disabled value={((parseFloat(formData.hours_acquired) || 0) - (parseFloat(formData.hours_used) || 0)).toFixed(2) + p('hours_suffix')} className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-2.5 text-white/50" /></div>
              <div><label className={modalForm.label}>{<RequiredLabelText text={p('label_acquisition_date_required')} />}</label><CustomDatePicker value={formData.acquisition_date || ''} onChange={(value) => setFormData({ ...formData, acquisition_date: value, expiration_date: value ? getEndOfMonthDate(value) : formData.expiration_date })} placeholder={p('select')} /></div>
              <div><label className={modalForm.label}>{<RequiredLabelText text={p('label_acquisition_reason_required')} />}</label><CustomSelect value={formData.acquisition_reason} onChange={(value) => setFormData({ ...formData, acquisition_reason: value })} options={acquisitionReasonOptions} placeholder={p('select')} searchable={true} creatable={true} autoSelect={false} /></div>
            </div>
            <div className={POINTAGE_FORM_GRID_CLASS}>
              <div><label className={modalForm.label}>{p('label_recovery_date')}</label><CustomDatePicker value={formData.recovery_date || ''} onChange={(value) => setFormData({ ...formData, recovery_date: value })} placeholder={p('select')} /></div>
              <div><label className={modalForm.label}>{p('label_expiration_date')}</label><CustomDatePicker value={formData.expiration_date || ''} onChange={(value) => setFormData({ ...formData, expiration_date: value })} placeholder={p('select')} /></div>
            </div>
            </div>
          )}
          {pointageFormTabVisible(recuperationFormTabs, formTab, 'details') && (
            <div className={POINTAGE_FORM_GRID_CLASS}>
              <div><label className={modalForm.label}>{<RequiredLabelText text={p('label_status_required')} />}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: s.icon, isDefault: true }))} placeholder={p('select')} searchable={true} creatable={false} autoSelect={false} /></div>
              <div className="md:col-span-3"><label className={modalForm.label}>{p('section_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className={modalForm.textarea} placeholder={p('placeholder_notes')}></textarea></div>
            </div>
          )}
        </form>
      </QawaemErpCrudModal>
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default RecuperationsPage;

